import POKEMON_ADDITIONS_RAW from '../../../shared/pokemon-additions.json'
import {
  computeCombatEffectiveDps,
  formatPowerDisplay,
  roundPower,
  roundPowerForDisplay,
  toCombatDps,
} from '../../../shared/power_display'
export {
  computeCombatEffectiveDps,
  formatPowerDisplay,
  roundPower,
  roundPowerForDisplay,
  toCombatDps,
}
export type { FormatPowerDisplayOptions } from '../../../shared/power_display'
import { getEvoStageMult } from '~/data/evolutions'
import { getPokemonTypes, getTypeEffectiveness } from '~/data/types'
import { battleTowerTrialOffenseTypeMult } from '~/data/battleTower'
import { getRarity, getSlugGeneration, getStarDpsMult, type Rarity } from '~/data/gacha'
import type { PokemonType } from '~/data/types'

export type { DisplayRarity } from './rarityTheme'
import type { DisplayRarity } from './rarityTheme'
export type PokemonTrait =
  | 'mega'
  | 'uber_mega'
  | 'gmax'
  | 'mythic'
  | 'divine'
  | 'primal'
  | 'origin'
  | 'complete'
  | 'ultra'
  | 'eternamax'
  | 'crowned'
  | 'fusion'

type PokemonAddition = {
  slug: string
  baseSlug: string
  formKind: string
  rarity?: Rarity | string
}

type PokemonAdditionsConfig = {
  pokemon: Record<string, PokemonAddition>
}

const ADDITIONS = POKEMON_ADDITIONS_RAW as PokemonAdditionsConfig
const ADDITION_BY_SLUG = ADDITIONS.pokemon

export const DISPLAY_RARITIES: DisplayRarity[] = ['common', 'rare', 'epic', 'mythic', 'legendary', 'divine']

export { DISPLAY_RARITY_COLORS } from './rarityTheme'

export const DISPLAY_RARITY_LABELS_FR: Record<DisplayRarity, string> = {
  common: 'Commun',
  rare: 'Rare',
  epic: 'Épique',
  mythic: 'Mythique',
  legendary: 'Légendaire',
  divine: 'Divin',
}

export const DISPLAY_RARITY_LABELS_EN: Record<DisplayRarity, string> = {
  common: 'Common',
  rare: 'Rare',
  epic: 'Epic',
  mythic: 'Mythic',
  legendary: 'Legendary',
  divine: 'Divine',
}

export const DISPLAY_RARITY_SORT_ORDER: Record<DisplayRarity, number> = {
  divine: 0,
  legendary: 1,
  mythic: 2,
  epic: 3,
  rare: 4,
  common: 5,
}

const POWER_TIER_MULT: Record<Rarity, number> = {
  common: 1,
  rare: 1.5,
  epic: 2,
  mega: 2.5,
  gmax: 2.5,
  mythic: 3.5,
  legendary: 4,
  uber_mega: 4,
  divine: 5,
}

const LEGENDARY_RARITIES = new Set<Rarity>(['mythic', 'legendary', 'uber_mega', 'divine'])
const POWER_FORM_RARITIES = new Set<Rarity>(['mega', 'gmax', 'uber_mega'])
const DIVINE_APEX_TRAITS = new Set<PokemonTrait>(['mega', 'primal', 'ultra', 'eternamax', 'complete'])
/** Bonus minimum d'une forme Gmax/Mega équipée vs la même unité sans forme (aligné trait ×1,2). */
const ACTIVE_FORM_MIN_BOOST = 1.2

export type PokemonPowerInput = {
  slug: string
  cosmeticFormSlug?: string | null
  level: number
  stars: number
  isShiny: boolean
  rarity?: Rarity | string | null
}

export type PokemonPowerBreakdown = {
  baseSlug: string
  formSlug: string | null
  displayRarity: DisplayRarity
  traits: PokemonTrait[]
  baseDamage: number
  /** Multiplicateur issu de la ligne évolutive (×1 / ×1.3 / ×1.5), avant neutralisation forme puissante. */
  lineEvolutionMult: number
  evolutionMult: number
  powerTierMult: number
  traitMult: number
  cappedClassMult: number
  shinyMult: number
  starMult: number
  rawPower: number
  power: number
}

function normalizeRarity(value: unknown): Rarity {
  switch (value) {
    case 'common':
    case 'rare':
    case 'epic':
    case 'mega':
    case 'gmax':
    case 'mythic':
    case 'legendary':
    case 'uber_mega':
    case 'divine':
      return value
    default:
      return 'common'
  }
}

function additionFor(slug: string | null | undefined): PokemonAddition | null {
  if (!slug) return null
  return ADDITION_BY_SLUG[slug] ?? null
}

function configuredRarityForSlug(slug: string | null | undefined): Rarity | null {
  if (!slug) return null
  const rarity = normalizeRarity(getRarity(slug))
  return rarity === 'common' ? null : rarity
}

export function isMegaFormSlug(slug: string): boolean {
  return slug.endsWith('-mega') || /-mega[xyz]$/.test(slug)
}

/** Mythique+ hors gacha (ex. Shifours, Melmetal) : la forme Gmax/Mega hérite du tier de base pour le DPS. */
function resolveHighTierBaseRarity(
  input: { slug: string; cosmeticFormSlug?: string | null; rarity?: Rarity | string | null },
  baseSlug: string,
): Rarity | null {
  const candidates: Rarity[] = []
  const baseConfigured = configuredRarityForSlug(baseSlug)
  if (baseConfigured) candidates.push(baseConfigured)
  const stored = normalizeRarity(input.rarity)
  if (stored !== 'common') candidates.push(stored)

  for (const rarity of candidates) {
    if (LEGENDARY_RARITIES.has(rarity)) return rarity
  }
  return null
}

function getEffectiveRarity(input: { slug: string; cosmeticFormSlug?: string | null; rarity?: Rarity | string | null }): Rarity {
  const formSlug = input.cosmeticFormSlug || input.slug
  const formKind = resolveFormKind(input.slug, input.cosmeticFormSlug)
  const formAddition = additionFor(formSlug)
  let formRarity: Rarity | null = null
  if (formAddition?.rarity) {
    formRarity = normalizeRarity(formAddition.rarity)
  } else {
    const configured = configuredRarityForSlug(formSlug)
    if (configured && formSlug !== input.slug) formRarity = configured
  }

  if (
    (formKind === 'mega' || formKind === 'gmax') &&
    formRarity &&
    (formRarity === 'mega' || formRarity === 'gmax')
  ) {
    const inherited = resolveHighTierBaseRarity(
      input,
      resolvePowerBaseSlug(input.slug, input.cosmeticFormSlug),
    )
    if (inherited) return inherited
  }

  if (formRarity) return formRarity
  return normalizeRarity(input.rarity ?? getRarity(input.slug))
}

export function resolvePowerBaseSlug(slug: string, cosmeticFormSlug?: string | null): string {
  const form = additionFor(cosmeticFormSlug) ?? additionFor(slug)
  if (form?.baseSlug) return form.baseSlug
  const formSlug = cosmeticFormSlug || slug
  if (formSlug.endsWith('-gmax')) return formSlug.replace(/-gmax$/, '')
  if (formSlug.endsWith('-megax') || formSlug.endsWith('-megay') || formSlug.endsWith('-megaz')) {
    return formSlug.replace(/-mega[xyz]$/, '')
  }
  if (formSlug.endsWith('-mega')) return formSlug.replace(/-mega$/, '')
  return slug
}

function resolveFormKind(slug: string, cosmeticFormSlug?: string | null): string | null {
  const form = additionFor(cosmeticFormSlug) ?? additionFor(slug)
  if (form?.formKind) return form.formKind
  const formSlug = cosmeticFormSlug || slug
  if (isMegaFormSlug(formSlug)) return 'mega'
  if (formSlug.includes('-gmax')) return 'gmax'
  return null
}

export function getDisplayRarity(input: { slug: string; cosmeticFormSlug?: string | null; rarity?: Rarity | string | null }): DisplayRarity {
  const rarity = getEffectiveRarity(input)
  const formKind = resolveFormKind(input.slug, input.cosmeticFormSlug)
  if ((formKind === 'mega' || formKind === 'gmax') && (rarity === 'common' || rarity === 'mega' || rarity === 'gmax')) {
    const baseSlug = resolvePowerBaseSlug(input.slug, input.cosmeticFormSlug)
    const baseRarity = normalizeRarity(getRarity(baseSlug))
    if (baseRarity === 'rare' || baseRarity === 'epic') return baseRarity
    if (baseRarity === 'mythic') return 'mythic'
    if (baseRarity === 'divine') return 'divine'
    if (LEGENDARY_RARITIES.has(baseRarity)) return 'legendary'
    // baseSlug absent du pool gacha : utiliser la rareté stockée du Pokémon comme fallback
    // (ex. Urshifu évolué d'un Kubfu mythique, Melmetal évolué d'un Meltan mythique)
    const storedRarity = normalizeRarity(input.rarity)
    if (storedRarity === 'rare' || storedRarity === 'epic') return storedRarity
    if (storedRarity === 'mythic') return 'mythic'
    if (storedRarity === 'divine') return 'divine'
    if (LEGENDARY_RARITIES.has(storedRarity)) return 'legendary'
    return 'epic'
  }
  if (rarity === 'common' || rarity === 'rare' || rarity === 'epic' || rarity === 'mythic' || rarity === 'divine') return rarity
  if (rarity === 'uber_mega' || rarity === 'legendary') return 'legendary'
  if (rarity === 'mega' || rarity === 'gmax') return 'epic'

  const baseSlug = resolvePowerBaseSlug(input.slug, input.cosmeticFormSlug)
  const baseRarity = normalizeRarity(getRarity(baseSlug))
  if ((rarity === 'mega' || rarity === 'gmax') && baseSlug !== input.slug && baseRarity === 'common') {
    return 'epic'
  }
  if (baseRarity === 'common' || baseRarity === 'rare' || baseRarity === 'epic' || baseRarity === 'mythic' || baseRarity === 'divine') return baseRarity
  if (LEGENDARY_RARITIES.has(baseRarity)) return 'legendary'

  // Gmax forms are special raid forms. If the base rarity is unknown, keep them in Epic.
  if (rarity === 'gmax') return 'epic'
  if (rarity === 'mega') return 'epic'
  return 'common'
}

/**
 * Tri « par rareté » : au sein d’une même rareté d’affichage, les formes Méga / Uber Méga / Gigamax
 * (badges visibles) passent avant les autres (ex. Épiques Méga au-dessus des Épiques classiques).
 */
export function megaGigamaxDisplaySortPriority(input: {
  slug: string
  cosmeticFormSlug?: string | null
  rarity?: Rarity | string | null
}): number {
  for (const trait of getPokemonTraits(input)) {
    if (trait === 'mega' || trait === 'uber_mega' || trait === 'gmax') return 1
  }
  return 0
}

export function getPokemonTraits(input: { slug: string; cosmeticFormSlug?: string | null; rarity?: Rarity | string | null }): PokemonTrait[] {
  const rarity = getEffectiveRarity(input)
  const traits = new Set<PokemonTrait>()
  const formSlug = input.cosmeticFormSlug || input.slug
  const formKind = resolveFormKind(input.slug, input.cosmeticFormSlug)

  if (rarity === 'uber_mega') traits.add('uber_mega')
  else if (formKind === 'mega' || rarity === 'mega' || isMegaFormSlug(formSlug)) traits.add('mega')
  if (formKind === 'gmax' || rarity === 'gmax' || formSlug.includes('-gmax')) traits.add('gmax')
  if (formSlug.includes('primal')) traits.add('primal')
  if (formSlug.includes('origin')) traits.add('origin')
  if (formSlug.includes('complete')) traits.add('complete')
  if (formSlug.includes('ultra')) traits.add('ultra')
  if (formSlug.includes('eternamax') || formSlug.includes('infinimax')) traits.add('eternamax')
  if (formSlug.includes('crowned')) traits.add('crowned')
  if (formSlug.includes('kyurem-') || formSlug.includes('necrozma-') || formSlug.includes('calyrex-')) traits.add('fusion')

  return [...traits]
}

function getControlledTraitMult(rarity: Rarity, traits: PokemonTrait[], formKind: string | null): number {
  const traitSet = new Set(traits)
  if (rarity === 'uber_mega' && traitSet.has('uber_mega')) return 1.2
  if (rarity === 'mega' && traitSet.has('mega')) return 1.2
  if (rarity === 'gmax' && traitSet.has('gmax')) return 1.2
  if (rarity === 'divine' && traits.some((trait) => DIVINE_APEX_TRAITS.has(trait))) return 1.2
  if (rarity === 'mythic' && formKind === 'mega' && traitSet.has('mega')) return 1.2
  if (
    LEGENDARY_RARITIES.has(rarity) &&
    formKind === 'gmax' &&
    traitSet.has('gmax')
  ) {
    return 1.2
  }
  return 1
}

/** Gmax/Mega débloqués sur une base mythique+ : on garde le bonus de stade (la forme doit booster le DPS). */
function keepsEvolutionMultWithActiveForm(formKind: string | null, effectiveRarity: Rarity): boolean {
  return (formKind === 'gmax' || formKind === 'mega') && LEGENDARY_RARITIES.has(effectiveRarity)
}

function computeCappedClassMult(input: {
  effectiveRarity: Rarity
  formKind: string | null
  traits: PokemonTrait[]
  lineEvolutionMult: number
}): {
  evolutionMult: number
  powerTierMult: number
  traitMult: number
  cappedClassMult: number
} {
  const { effectiveRarity, formKind, traits, lineEvolutionMult } = input
  const hasActiveForm = formKind === 'gmax' || formKind === 'mega'
  const isPowerForm =
    POWER_FORM_RARITIES.has(effectiveRarity) ||
    traits.some((trait) => DIVINE_APEX_TRAITS.has(trait)) ||
    hasActiveForm
  const evolutionMult = keepsEvolutionMultWithActiveForm(formKind, effectiveRarity)
    ? lineEvolutionMult
    : isPowerForm
      ? 1
      : lineEvolutionMult
  const powerTierMult = POWER_TIER_MULT[effectiveRarity]
  const traitMult = getControlledTraitMult(effectiveRarity, traits, formKind)
  return {
    evolutionMult,
    powerTierMult,
    traitMult,
    cappedClassMult: evolutionMult * powerTierMult * traitMult,
  }
}

export function roundMultiplier(value: number, precision = 2): number {
  const safe = Number.isFinite(value) ? value : 1
  const factor = 10 ** precision
  return Math.round(safe * factor) / factor
}

export function formatMultiplier(value: number): string {
  const rounded = roundMultiplier(value)
  if (Number.isInteger(rounded)) return String(rounded)
  return rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
}

export function getPokemonPowerBreakdown(pokemon: PokemonPowerInput): PokemonPowerBreakdown {
  const baseSlug = resolvePowerBaseSlug(pokemon.slug, pokemon.cosmeticFormSlug)
  const formSlug = pokemon.cosmeticFormSlug || (baseSlug !== pokemon.slug ? pokemon.slug : null)
  const effectiveRarity = getEffectiveRarity(pokemon)
  const formKind = resolveFormKind(pokemon.slug, pokemon.cosmeticFormSlug)
  const displayRarity = getDisplayRarity(pokemon)
  const traits = getPokemonTraits(pokemon)
  const level = Math.max(1, Math.min(100, Math.floor(Number(pokemon.level) || 1)))
  const stars = Math.max(1, Math.min(5, Math.floor(Number(pokemon.stars) || 1)))
  const baseDamage = level * 2
  const lineEvolutionMult = getEvoStageMult(baseSlug)
  const classMult = computeCappedClassMult({
    effectiveRarity,
    formKind,
    traits,
    lineEvolutionMult,
  })
  let { evolutionMult, powerTierMult, traitMult, cappedClassMult } = classMult

  // Gmax/Mega : strictement plus fort que la forme de base (+20 % minimum).
  if (formKind === 'gmax' || formKind === 'mega') {
    const baseClassMult = computeCappedClassMult({
      effectiveRarity: getEffectiveRarity({ ...pokemon, cosmeticFormSlug: null }),
      formKind: null,
      traits: getPokemonTraits({ ...pokemon, cosmeticFormSlug: null }),
      lineEvolutionMult,
    })
    cappedClassMult = Math.max(
      cappedClassMult,
      baseClassMult.cappedClassMult * ACTIVE_FORM_MIN_BOOST,
    )
  }

  const shinyMult = pokemon.isShiny ? 4 : 1
  const starMult = getStarDpsMult(stars, pokemon.isShiny)
  const rawPower = baseDamage * cappedClassMult * shinyMult * starMult

  return {
    baseSlug,
    formSlug,
    displayRarity,
    traits,
    baseDamage,
    lineEvolutionMult,
    evolutionMult,
    powerTierMult,
    traitMult,
    cappedClassMult,
    shinyMult,
    starMult,
    rawPower,
    power: Math.round(rawPower),
  }
}

export function getPokemonPower(pokemon: PokemonPowerInput): number {
  return getPokemonPowerBreakdown(pokemon).power
}

/** Même règle que useCombatLoop : hors région (gen < zone de combat) → ×0,001 tant que pas endgame. */
export function getRegionDpsMultiplier(
  slug: string,
  cosmeticFormSlug: string | null | undefined,
  combatGen: number,
  isEndgame: boolean,
): number {
  const pokeGen = getSlugGeneration(resolvePowerBaseSlug(slug, cosmeticFormSlug))
  if (isEndgame) return 1
  return pokeGen >= combatGen ? 1 : 0.001
}

export function isPokemonEligibleForCombatRegion(
  pokemon: Pick<PokemonPowerInput, 'slug' | 'cosmeticFormSlug'>,
  combatGen: number,
  isEndgame: boolean,
): boolean {
  if (isEndgame) return true
  const pokeGen = getSlugGeneration(resolvePowerBaseSlug(pokemon.slug, pokemon.cosmeticFormSlug))
  return pokeGen >= combatGen
}

/** DPS effectif d'un Pokémon vs types ennemis (région + types), aligné sur le combat. */
export function estimatePokeEffectiveDpsVsTypes(
  pokemon: PokemonPowerInput,
  defenderTypes: PokemonType[],
  opts: {
    combatGen: number
    isEndgame: boolean
    battleTowerZone?: number | null
  },
): number {
  const regionMult = getRegionDpsMultiplier(
    pokemon.slug,
    pokemon.cosmeticFormSlug,
    opts.combatGen,
    opts.isEndgame,
  )
  const permanentDps = Math.round(getPokemonPower(pokemon) * regionMult)
  if (defenderTypes.length === 0) return toCombatDps(permanentDps)

  const attackTypes = getPokemonTypes(pokemon)
  let typeMult = Math.max(...attackTypes.map((atk) => getTypeEffectiveness(atk, defenderTypes)))
  typeMult = battleTowerTrialOffenseTypeMult(typeMult, opts.battleTowerZone ?? null)
  return toCombatDps(permanentDps * typeMult)
}

export function getTeamPower(team: PokemonPowerInput[]): number {
  return team.reduce((sum, pokemon) => sum + getPokemonPower(pokemon), 0)
}

export function getRaidTypeMultiplier(pokemon: { slug: string; cosmeticFormSlug?: string | null }, defenderTypes: PokemonType[]): number {
  if (defenderTypes.length === 0) return 1
  const attackTypes = pokemon.cosmeticFormSlug
    ? getPokemonTypes({
        slug: resolvePowerBaseSlug(pokemon.slug, pokemon.cosmeticFormSlug),
        cosmeticFormSlug: pokemon.cosmeticFormSlug,
      })
    : getPokemonTypes(pokemon.slug)
  const best = Math.max(...attackTypes.map((type) => getTypeEffectiveness(type, defenderTypes)))
  if (best >= 2) return 1.25
  if (best > 1) return 1.15
  if (best < 1) return 0.9
  return 1
}

export function getRaidPower(team: PokemonPowerInput[], defenderTypes: PokemonType[] = []): number {
  const total = team.reduce(
    (sum, pokemon) => sum + getPokemonPower(pokemon) * getRaidTypeMultiplier(pokemon, defenderTypes),
    0,
  )
  return toCombatDps(total)
}

export function getExpeditionPower(team: PokemonPowerInput[], context?: { generation?: number; types?: PokemonType[] }): number {
  const destinationTypes = context?.types ?? []
  const destinationGen = context?.generation
  const total = team.reduce((sum, pokemon) => {
    let mult = 1
    if (destinationTypes.length > 0) {
      mult *= getRaidTypeMultiplier(pokemon, destinationTypes)
    }
    if (destinationGen && getSlugGeneration(resolvePowerBaseSlug(pokemon.slug, pokemon.cosmeticFormSlug)) === destinationGen) {
      mult *= 1.05
    }
    return sum + getPokemonPower(pokemon) * mult
  }, 0)
  return toCombatDps(total)
}
